import { useState } from "react";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import DatePicker from "@/components/DatePicker";
import api, { formatDetail } from "@/lib/api";

const MONTHS_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

/**
 * HR monthly/range XLSX report downloader. Extracted from LeaveRequestsPage.jsx per
 * code review (session 149-c). Uses axios (dual-mode auth — cookies + Bearer via
 * interceptor) instead of manual `fetch` with `localStorage.getItem("ens_token")`.
 */
export default function ReportDownloader({ reportMonth, setReportMonth, reportYear, setReportYear }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [busy, setBusy] = useState(false);

  const download = async () => {
    const params = new URLSearchParams();
    if (mode === "month") {
      params.set("year", String(reportYear));
      params.set("month", String(reportMonth));
    } else {
      if (!startDate || !endDate) { toast.error("Tanggal mulai & selesai wajib diisi"); return; }
      if (endDate < startDate) { toast.error("Tanggal selesai harus setelah tanggal mulai"); return; }
      params.set("start", startDate);
      params.set("end", endDate);
    }
    setBusy(true);
    try {
      const res = await api.get(`/reports/leave-monthly-xlsx?${params.toString()}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      const suffix = mode === "month"
        ? `${reportYear}_${String(reportMonth).padStart(2, "0")}`
        : `${startDate.replace(/-/g, "")}_${endDate.replace(/-/g, "")}`;
      a.download = `laporan_hr_${suffix}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Laporan diunduh");
      setOpen(false);
    } catch (e) {
      const detail = e.response?.data;
      if (detail instanceof Blob) {
        try { const txt = await detail.text(); const j = JSON.parse(txt); toast.error(formatDetail(j.detail) || "Gagal mengunduh laporan"); }
        catch { toast.error("Gagal mengunduh laporan"); }
      } else { toast.error(formatDetail(detail?.detail) || "Gagal mengunduh laporan"); }
    }
    finally { setBusy(false); }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  return (
    <>
      <button
        data-testid="btn-download-report"
        onClick={() => setOpen(true)}
        className="rounded-none border border-[#0B1D3A] bg-white h-9 px-3 text-xs uppercase tracking-wider font-semibold text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white inline-flex items-center gap-1"
      >
        <FileDown size={13} /> Laporan HR
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="dlg-report-download">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><FileDown size={18} className="text-[#FF5A1F]" /> Unduh Laporan HR</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-1">
              <button
                type="button"
                data-testid="btn-mode-month"
                onClick={() => setMode("month")}
                className={`text-xs px-3 py-2 border font-semibold uppercase tracking-wider ${mode === "month" ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1D3A]"}`}
              >Per Bulan</button>
              <button
                type="button"
                data-testid="btn-mode-range"
                onClick={() => setMode("range")}
                className={`text-xs px-3 py-2 border font-semibold uppercase tracking-wider ${mode === "range" ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1D3A]"}`}
              >Per Periode</button>
            </div>
            {mode === "month" ? (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Bulan</label>
                  <Select value={String(reportMonth)} onValueChange={(v) => setReportMonth(Number(v))}>
                    <SelectTrigger data-testid="select-report-month" className="rounded-none h-9 mt-1 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {MONTHS_ID.map((m, i) => (<SelectItem key={i + 1} value={String(i + 1)} className="text-xs">{m}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Tahun</label>
                  <Select value={String(reportYear)} onValueChange={(v) => setReportYear(Number(v))}>
                    <SelectTrigger data-testid="select-report-year" className="rounded-none h-9 mt-1 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {years.map((y) => (<SelectItem key={y} value={String(y)} className="text-xs">{y}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Tanggal Mulai *</label>
                  <DatePicker value={startDate} onChange={setStartDate} testid="dp-report-start" placeholder="Pilih…" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Tanggal Selesai *</label>
                  <DatePicker value={endDate} onChange={setEndDate} testid="dp-report-end" placeholder="Pilih…" />
                </div>
              </div>
            )}
            <div className="text-[10px] text-slate-500">File: Excel (.xlsx) · 2 sheet (Ringkasan per personil + Detail).</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Batal</Button>
            <Button data-testid="btn-confirm-download-report" onClick={download} disabled={busy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white">
              <FileDown size={14} className="mr-1" /> {busy ? "Menyiapkan…" : "Unduh"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
